import { useState, useEffect, useCallback } from "react";
import {
  Zap,
  StopCircle,
  AlertCircle,
  Plus,
  Clock,
  TrendingUp,
  ShoppingCart,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { KPICard, AdminModal, EmptyState } from "@/components/admin/shared";
import { MarketingImageEditor } from "@/components/admin/images";
import {
  useFlashSales,
  useCreateFlashSale,
  useEndFlashSale,
} from "@/hooks/admin/useMarketing";
import type {
  FlashSale,
  FlashSaleStatus,
  CreateFlashSaleInput,
} from "@/lib/admin-api/marketing";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<FlashSaleStatus, string> = {
  upcoming: "À venir",
  active: "Active",
  ended: "Terminée",
};

const STATUS_STYLES: Record<FlashSaleStatus, string> = {
  upcoming: "bg-blue-100 text-blue-800 border-blue-200",
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  ended: "bg-stone-100 text-stone-600 border-stone-200",
};

// ---------------------------------------------------------------------------
// Countdown timer
// ---------------------------------------------------------------------------

interface TimeLeft {
  readonly days: number;
  readonly hours: number;
  readonly minutes: number;
  readonly seconds: number;
}

function computeTimeLeft(targetDate: string): TimeLeft | null {
  const diff = new Date(targetDate).getTime() - Date.now();
  if (diff <= 0) return null;

  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

function CountdownTimer({ targetDate }: { readonly targetDate: string }) {
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(() =>
    computeTimeLeft(targetDate),
  );

  const updateTimer = useCallback(() => {
    setTimeLeft(computeTimeLeft(targetDate));
  }, [targetDate]);

  useEffect(() => {
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [updateTimer]);

  if (!timeLeft) {
    return <span className="text-xs text-stone-400">Terminée</span>;
  }

  const pad = (n: number): string => String(n).padStart(2, "0");

  return (
    <div className="flex items-center gap-1">
      <Clock className="h-3 w-3 text-amber-600" />
      <div className="flex items-baseline gap-0.5 font-mono text-xs tabular-nums">
        {timeLeft.days > 0 && (
          <span className="text-stone-700 font-semibold">
            {timeLeft.days}j
          </span>
        )}
        <span className="text-stone-700 font-semibold">
          {pad(timeLeft.hours)}:{pad(timeLeft.minutes)}:{pad(timeLeft.seconds)}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Active sale card (highlighted)
// ---------------------------------------------------------------------------

function ActiveSaleCard({
  sale,
  onEnd,
}: {
  readonly sale: FlashSale;
  readonly onEnd: (id: string) => void;
}) {
  return (
    <div className="rounded-xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-amber-600" />
            <h3 className="font-display font-semibold text-stone-900">
              {sale.name}
            </h3>
          </div>
          <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
            En cours
          </span>
        </div>
        <Button
          variant="outline"
          onClick={() => onEnd(sale.id)}
          className="shrink-0 text-xs"
        >
          <StopCircle className="mr-1.5 h-3.5 w-3.5" />
          Terminer
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm text-stone-600">Temps restant :</span>
        <CountdownTimer targetDate={sale.ends_at} />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg bg-white/70 p-3 text-center">
          <p className="text-xs text-stone-500">Remise</p>
          <p className="font-display text-lg font-semibold text-amber-700 tabular-nums">
            -{sale.discount_percent} %
          </p>
        </div>
        <div className="rounded-lg bg-white/70 p-3 text-center">
          <p className="text-xs text-stone-500">Produits</p>
          <p className="font-display text-lg font-semibold text-stone-800 tabular-nums">
            {sale.product_count}
          </p>
        </div>
        <div className="rounded-lg bg-white/70 p-3 text-center">
          <p className="text-xs text-stone-500">Commandes</p>
          <p className="font-display text-lg font-semibold text-stone-800 tabular-nums">
            {(sale.total_orders ?? 0).toLocaleString("fr-SN")}
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create flash sale modal
// ---------------------------------------------------------------------------

function CreateFlashSaleModal({
  open,
  onClose,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
}) {
  const create = useCreateFlashSale();
  const [name, setName] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [discountPercent, setDiscountPercent] = useState("");
  const [productIds, setProductIds] = useState("");
  const [stockLimit, setStockLimit] = useState("");
  const [imageUrl, setImageUrl] = useState("");

  const handleSubmit = () => {
    if (!name.trim()) {
      toast.error("Le nom est obligatoire.");
      return;
    }
    if (!startsAt || !endsAt) {
      toast.error("Les dates sont obligatoires.");
      return;
    }
    if (new Date(endsAt) <= new Date(startsAt)) {
      toast.error("La date de fin doit être postérieure au début.");
      return;
    }
    const dp = parseFloat(discountPercent);
    if (isNaN(dp) || dp <= 0 || dp > 100) {
      toast.error("Le pourcentage de remise doit être entre 1 et 100.");
      return;
    }

    const ids = productIds
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (ids.length === 0) {
      toast.error("Ajoutez au moins un produit.");
      return;
    }

    const input: CreateFlashSaleInput = {
      name,
      starts_at: startsAt,
      ends_at: endsAt,
      discount_percent: dp,
      product_ids: ids,
    };
    create.mutate(input, {
      onSuccess: () => {
        toast.success("Vente flash creee.");
        onClose();
      },
      onError: () => toast.error("Erreur lors de la creation."),
    });
  };

  // Duration preview
  const durationText = (() => {
    if (!startsAt || !endsAt) return null;
    const start = new Date(startsAt);
    const end = new Date(endsAt);
    const diffMs = end.getTime() - start.getTime();
    if (diffMs <= 0) return "Duree invalide";
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const mins = Math.floor((diffMs / (1000 * 60)) % 60);
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      const remHours = hours % 24;
      return `${days} jour(s) et ${remHours}h`;
    }
    return `${hours}h${mins > 0 ? ` ${mins}min` : ""}`;
  })();

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title="Nouvelle vente flash"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={create.isPending}>
            {create.isPending ? "En cours..." : "Creer"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Nom <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ex : Flash Vendredi 50%"
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="text-sm font-medium text-stone-700">
              Debut <span className="text-red-500">*</span>
            </label>
            <input
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-stone-700">
              Fin <span className="text-red-500">*</span>
            </label>
            <input
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              min={startsAt || undefined}
              className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>
        </div>

        {durationText && (
          <div className="flex items-center gap-2 rounded-lg bg-stone-50 px-3 py-2">
            <Clock className="h-3.5 w-3.5 text-stone-500" />
            <span className="text-xs text-stone-600">
              Duree : {durationText}
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="text-sm font-medium text-stone-700">
              Remise (%) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={discountPercent}
              onChange={(e) => setDiscountPercent(e.target.value)}
              min={1}
              max={100}
              placeholder="ex : 30"
              className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-stone-700">
              Stock limite par deal
            </label>
            <input
              type="number"
              value={stockLimit}
              onChange={(e) => setStockLimit(e.target.value)}
              min={1}
              placeholder="Illimite si vide"
              className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
            <p className="text-xs text-stone-400">
              Nombre max d'unites vendues a ce prix flash.
            </p>
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            IDs produits <span className="text-red-500">*</span>
          </label>
          <textarea
            value={productIds}
            onChange={(e) => setProductIds(e.target.value)}
            rows={2}
            placeholder="uuid-1, uuid-2, uuid-3"
            className="w-full resize-none rounded-lg border border-stone-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
          <p className="text-xs text-stone-400">
            Separes par des virgules
          </p>
        </div>

        {/* Banner image for the flash sale */}
        <MarketingImageEditor
          preset="flash-sale"
          value={imageUrl}
          onChange={(url) => setImageUrl(url)}
          label="Image de la vente flash (optionnel)"
        />
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function FlashSales() {
  const [statusFilter, setStatusFilter] = useState<FlashSaleStatus | "">("");
  const [modalOpen, setModalOpen] = useState(false);
  const { data, isLoading, isError } = useFlashSales(
    statusFilter ? { status: statusFilter } : {},
  );
  const endSale = useEndFlashSale();

  const sales = data?.flash_sales ?? [];
  const activeSales = sales.filter((s) => s.status === "active");
  const upcomingSales = sales.filter((s) => s.status === "upcoming");
  const endedSales = sales.filter((s) => s.status === "ended");
  const totalOrders = sales.reduce((acc, s) => acc + s.total_orders, 0);

  const handleEnd = (id: string) => {
    endSale.mutate(id, {
      onSuccess: () => toast.success("Vente flash terminee."),
      onError: () => toast.error("Erreur lors de la terminaison."),
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <Zap className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Ventes flash
              </h1>
              <p className="text-sm text-stone-500">
                Offres a duree limitee sur des produits selectionnes
              </p>
            </div>
          </div>
          <Button onClick={() => setModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nouvelle vente flash
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard
            label="Actives"
            value={activeSales.length}
            format="number"
            icon={<Zap className="h-4 w-4" />}
          />
          <KPICard label="À venir" value={upcomingSales.length} format="number" />
          <KPICard label="Terminées" value={endedSales.length} format="number" />
          <KPICard
            label="Commandes totales"
            value={totalOrders}
            format="number"
            icon={<ShoppingCart className="h-4 w-4" />}
          />
        </div>

        {/* Active sales - highlighted cards */}
        {activeSales.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-stone-700 uppercase tracking-wide">
              Ventes en cours
            </h2>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {activeSales.map((s) => (
                <ActiveSaleCard key={s.id} sale={s} onEnd={handleEnd} />
              ))}
            </div>
          </div>
        )}

        {/* Filter */}
        <div className="flex gap-3">
          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as FlashSaleStatus | "")
            }
            className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm text-stone-700 focus:outline-none focus:ring-2 focus:ring-amber-300"
          >
            <option value="">Tous les statuts</option>
            <option value="upcoming">À venir</option>
            <option value="active">Actives</option>
            <option value="ended">Terminées</option>
          </select>
        </div>

        {/* Table */}
        {isLoading && (
          <div className="space-y-3 animate-pulse">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-12 rounded bg-stone-100" />
            ))}
          </div>
        )}

        {isError && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            Impossible de charger les ventes flash.
          </div>
        )}

        {!isLoading && !isError && sales.length === 0 && (
          <EmptyState
            icon={<Zap className="h-6 w-6" />}
            title="Aucune vente flash"
            description="Creez une vente flash pour booster vos ventes en temps limite."
            action={{
              label: "Nouvelle vente flash",
              onClick: () => setModalOpen(true),
            }}
          />
        )}

        {!isLoading && !isError && sales.length > 0 && (
          <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100 bg-stone-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Nom
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Remise
                    </th>
                    <th className="hidden sm:table-cell px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Produits
                    </th>
                    <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Debut
                    </th>
                    <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Fin
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Statut
                    </th>
                    <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Compte a rebours
                    </th>
                    <th className="hidden lg:table-cell px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Commandes
                    </th>
                    <th className="w-24 px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {sales.map((s) => (
                    <tr
                      key={s.id}
                      className={`transition-colors ${
                        s.status === "active"
                          ? "bg-amber-50/30 hover:bg-amber-50/50"
                          : "hover:bg-stone-50"
                      }`}
                    >
                      <td className="px-4 py-3 font-medium text-stone-900">
                        {s.name}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-amber-700">
                        -{s.discount_percent} %
                      </td>
                      <td className="hidden sm:table-cell px-4 py-3 text-right tabular-nums text-stone-600">
                        {s.product_count}
                      </td>
                      <td className="hidden md:table-cell px-4 py-3 text-stone-500 text-xs">
                        {new Date(s.starts_at).toLocaleDateString("fr-SN")}
                      </td>
                      <td className="hidden md:table-cell px-4 py-3 text-stone-500 text-xs">
                        {new Date(s.ends_at).toLocaleDateString("fr-SN")}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLES[s.status]}`}
                        >
                          {STATUS_LABELS[s.status]}
                        </span>
                      </td>
                      <td className="hidden lg:table-cell px-4 py-3">
                        {s.status === "active" ? (
                          <CountdownTimer targetDate={s.ends_at} />
                        ) : s.status === "upcoming" ? (
                          <span className="text-xs text-blue-600">
                            Demarre dans{" "}
                            <CountdownTimer targetDate={s.starts_at} />
                          </span>
                        ) : (
                          <span className="text-xs text-stone-400">
                            \u2014
                          </span>
                        )}
                      </td>
                      <td className="hidden lg:table-cell px-4 py-3 text-right tabular-nums text-stone-600">
                        {(s.total_orders ?? 0).toLocaleString("fr-SN")}
                      </td>
                      <td className="px-4 py-3">
                        {s.status !== "ended" && (
                          <button
                            title="Terminer maintenant"
                            onClick={() => handleEnd(s.id)}
                            className="rounded-md p-1.5 text-stone-400 hover:bg-red-50 hover:text-red-700"
                          >
                            <StopCircle className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <CreateFlashSaleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </AdminLayout>
  );
}
